import { getBootstrapData } from "@swa-ui/bootstrap";
import { isResponseOk, useFetch } from "@swa-ui/fetch";
import { logger } from "@swa-ui/log";

/**
 * Hook that provides a function to fetch a signed JWT signature from the Southwest security endpoint.
 * Must be called before `setOrder()` — the returned signature is a required parameter.
 *
 * @param {string} jwtConfigKey - The id of the config entry in jwt-settings.configs to use as the endpoint
 * @returns {{ fetchCeptorJWT: Function, loading: boolean }}
 */
export const useFetchCeptorJWT = (jwtConfigKey) => {
  const { loading, post, response } = useFetch();

  return { fetchCeptorJWT, loading };

  /**
   * @param {string} params.amount - Dollar-denominated decimal string (e.g. "171.10"), NOT pennies
   * @param {string} params.currencyCode - ISO currency code (e.g. "USD")
   * @param {string} params.invoiceId - UUID v4 transaction identifier
   * @param {string} params.merchantCode - Southwest Airlines merchant code (e.g. "526")
   * @returns {Promise<string>} The JWT signature string
   * @throws {Error} If the endpoint returns a non-2xx status or the request fails
   */
  async function fetchCeptorJWT({ amount, currencyCode, invoiceId, merchantCode }) {
    try {
      const jwtSettings = getBootstrapData("jwt-settings");
      const endpoint = jwtSettings?.configs?.find(
        (config) => config.id === jwtConfigKey
      )?.endpointUrl;

      if (!endpoint) {
        throw new Error(
          `jwt-settings bootstrap config is missing or endpointUrl is not defined for config id: ${jwtConfigKey}`
        );
      }

      const signature = await post(endpoint, { amount, currencyCode, invoiceId, merchantCode });

      if (!isResponseOk(response)) {
        throw new Error(`Ceptor JWT fetch failed with status ${response.status}`);
      }

      return signature;
    } catch (error) {
      logger.error("fetchCeptorJWT failed", {
        component: "fetchCeptorJWT",
        error,
        invoiceId,
      });
      throw error;
    }
  }
};
